import { Component, Injector, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActionsSubject, Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    switchMap,
    take,
    tap
} from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportProvider,
    annualReportTemplateActions,
    AnnualReportTemplatesProvider,
    DEFAULT_AR_NAVIGATION,
    Errors,
    IAnnualReportTemplateDTO,
    onCreateAnnualReportSuccessfully,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully
} from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreLayoutProvider,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    LOCALE_LOCALIZATION_KEYS_MAP,
    ModalComponent,
    ModalData,
    ModalTitleIcons
} from '@modeso/aumico-lib-core-fe';
import { IFilterOptions, SettingsProvider } from '@modeso/aumico-lib-settings-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { AnnualReportSubPage } from '../annual-report-sub-page';

import {
    AnnualReportStep,
    IAnnualReport,
    IAnnualReportDto,
    IFinStatements,
    IGeneralInformation,
    ISetupData,
    ReportType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyDTO, ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import {
    IAccountingBasisDTO,
    IAccountingBasisExtendedDTO,
    ICurrencyDTO,
    ICurrencyExtendedDTO,
    ILegalFormDTO,
    ILegalFormExtendedDTO,
} from '@modeso/types__aumico-lib-settings-be';

import { BaseObjectsMapper } from './mappers/baseObjects.mapper';

import { HttpErrorResponse } from '@angular/common/http';
import { configurationsActions } from '@modeso/aumico-lib-core-fe/src/lib';
import { IAccountingBasis } from '@modeso/types__aumico-lib-settings-be/dist/models/interfaces/IAccountingBasis.interface';
import { ofType } from '@ngrx/effects';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import * as countries from 'i18n-iso-countries';
import { cloneDeep } from 'lodash-es';

import { GeneralInformationMode } from '../../../../../../aumico-lib-annualreport-fe/src/lib/enums/general-information-form-mode.enum';
import { IGeneralInformationV2Output } from '../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/general-information-form-output.interface';
import { ProjectsOverviewActions, ProjectsOverviewState } from '../../../../../../aumico-lib-projects-fe/src/lib';
import { selectActiveProjectId } from '../../../../../../aumico-lib-projects-fe/src/lib/reducers/projects-overview.reducer';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { CountriesMapper } from '../../../shared/mappers/countries.mapper';
import { CountriesService } from '../../../shared/services/countries.service';
import { templateConstants } from '../../../shared/template-constants';
import { localizationKeys } from '../../../utils/localizationKeys';
import { CompanyMapper } from '../mappers/company-mapper';
import { AnnualReportTemplateMapper } from './mappers/arTemplate.mapper';

const debug = Debug('aumico:project-reports-fe:AnnualReportGeneralInformationV2Page');

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-general-information-v2-page',
    templateUrl: './annual-report-general-information-v2.page.html',
    styleUrls: ['./annual-report-general-information-v2.page.scss'],
})
export class AnnualReportGeneralInformationV2Page extends AnnualReportSubPage implements OnInit {
    // Component state
    mode: GeneralInformationMode | undefined;
    setupData: ISetupData | undefined;
    currentAnnualReport: IAnnualReport | undefined;

    // Dropdown options
    companies: ICompanyNameIdLegalFormDTO[] = [];
    accountingBasesFilteredSelections: INameId[] = [];
    currenciesFilteredSelections: INameId[] = [];
    reportTypes: INameId[] = [];
    locales: INameId[] | undefined;
    arTemplatesOptions: INameId[] = [];

    //required data
    readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;
    private projectId: string | null = null;
    localizationKeys = localizationKeys;
    private countries: INameId[];
    private initializedCountryCode: string;
    private shortLegalForms: ILegalFormDTO[];
    accountingBases: IAccountingBasisDTO[] = [];
    private currencies: ICurrencyDTO[] = [];
    private originalGeneralInformation: IGeneralInformation;
    selectedCompany: ICompanyDTO | undefined;
    createdCompany: ICompanyNameIdLegalFormDTO;
    private accountingBasisSelected: IAccountingBasisDTO | undefined;
    private arTemplates: IAnnualReportTemplateDTO[] = [];
    arTemplateToBeSaved: IAnnualReportTemplateDTO | null = null;
    private accountingBasisSelectionChangedSubject = new Subject<void>();
    private hasPreviousDataChangedSubject = new Subject<boolean>();
    reportLocale: INameId;
    isCpmmActive = false;

    // required data for autofill
    previousAnnualReportOptions: Partial<IAnnualReportDto>[] = [];

    // Form data
    savedData: IGeneralInformationV2Output | undefined;
    private generalInformationOutput: IGeneralInformationV2Output | undefined;

    // Loading states
    private isSaveRequestInProgress: boolean;
    private isCompanySpecificDataLoading = false;
    // The AR templates of the selected company; null until they have arrived.
    private arTemplatesOfCompany: IAnnualReportTemplateDTO[] | null = null;
    // The country the accounting bases of the selected company are requested for; null until it is known.
    private countryOfCompany: string | null = null;
    // The countries each received accounting basis applies in, by id; an empty list means every country.
    private countriesOfAccountingBases = new Map<string, string[]>();
    // Tracks the company id we last loaded company-specific data for, so we don't reload
    // (and re-dispatch the company fetch via CountriesService) when the same company re-emits.
    private lastLoadedCompanyId: string | undefined;
    showSpinner = false;
    noArTemplateWarningDialogOpen = false;

    // Error handling
    isBackendReturnError = false;
    backendErrors: string;

    annualReportForBalanceAutoFill: IAnnualReport | undefined;
    annualReportForIncomeAutoFill: IAnnualReport | undefined;

    constructor(
        injector: Injector,
        private readonly settingsProvider: SettingsProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly annualReportTemplatesProvider: AnnualReportTemplatesProvider,
        private readonly baseObjectsMapper: BaseObjectsMapper,
        protected override actionsListener$: ActionsSubject,
        protected override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private readonly mixpanelService: MixpanelService,
        private readonly countriesService: CountriesService,
        private readonly countriesMapper: CountriesMapper,
        private readonly companyMapper: CompanyMapper,
        private readonly annualReportTemplateMapper: AnnualReportTemplateMapper,
        private i18next: I18NextService,
        dialog: MatDialog,
        private readonly dateLocalePipe: DateLocalePipe,
        private projectsStore: Store<ProjectsOverviewState>,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);
        this.currentAnnualReportStep = AnnualReportStep.GENERAL_INFORMATION;
        this.annualReportNavigationProvider.resetActiveArNavigation();
    }

    override ngOnInit(): void {
        debug('AnnualReportGeneralInformationV2Page ngOnInit called');
        this.mixpanelService.time_event(MIXPANEL_EVENTS.GENERAL_INFORMATION_TIME_SPENT_TILL_COMPLETION);

        super.ngOnInit();

        this.initializeDataLoading();

        this.setupSubscriptions();
    }

    private initializeDataLoading(): void {
        debug('initializeDataLoading called');
        // Load basic selections and data
        this.loadCurrencies();
        this.loadCountries();
        this.loadCompanies();
        this.loadAccountingBases();
        this.loadLegalForms();
        this.loadReportTypes();
        this.loadReportLocales();
    }

    private setupSubscriptions(): void {
        // Ensure organization is loaded
        this.organizationProvider.dispatchGetCurrentOrganization();

        // Listen for active annual report - use combineLatest to ensure both streams are considered
        const annualReport$ = this.annualReportProvider.getActiveAnnualReport$().pipe(
            filter((activeAnnual: IAnnualReport) => activeAnnual != null)
        );

        const organization$ = this.organizationProvider.getCurrentOrganization$().pipe(
            filter(organization => organization != null)
        );

        // Combine both streams so that it emits whenever either changes (and both are available).
        // Use switchMap (not mergeMap) for the sealed stream so we don't accumulate a new long-lived
        // inner subscription on every outer emission, and distinctUntilChanged so handleActiveAnnualReport
        // only re-runs when something it actually cares about changes. Without this guard, organization /
        // sealed re-emissions caused handleActiveAnnualReport to fire repeatedly, each time re-dispatching
        // the company fetch, AR-template fetch and autofill fetches (the "same request called many times").
        annualReport$.pipe(
            switchMap(activeAnnualReport =>
                organization$.pipe(
                    map(organization => ({ activeAnnualReport, organization }))
                )
            ),
            switchMap(({ activeAnnualReport, organization }) =>
                this.providerOfAnnualReports
                    .getAnnualReportIsSealed$()
                    .pipe(map((isSealed) => ({ activeAnnualReport, organization, isSealed }))),
            ),
            distinctUntilChanged((prev, curr) =>
                prev.activeAnnualReport._id === curr.activeAnnualReport._id &&
                (prev.activeAnnualReport.generalInformation != null) === (curr.activeAnnualReport.generalInformation != null) &&
                prev.isSealed === curr.isSealed &&
                prev.organization?.cpmmActivationDetails?.activationState === curr.organization?.cpmmActivationDetails?.activationState
            ),
            untilDestroyed(this),
        )
            .subscribe(({ activeAnnualReport, organization, isSealed }) => {
                if (!isSealed) {
                    this.isCpmmActive = organization.cpmmActivationDetails?.activationState === 'active';
                    this.handleActiveAnnualReport(activeAnnualReport);
                }
            });

        // Note: there is intentionally no separate subscription to load the report from the encrypted id.
        // getActiveAnnualReport$() already dispatches the load itself when no active report is present
        // (see provider), so an extra subscription here only duplicated the side-effectful autofill
        // dispatches without ever running its own (unreachable) body.

        // Listen for errors
        this.annualReportProvider
            .getError$()
            .pipe(untilDestroyed(this))
            .subscribe((error: HttpErrorResponse) => this.handleErrors(error));

        //Listen for annual report update
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        // Listen for onCreateAnnualReportSuccessfully to refresh data
        this.actionsListener$
            .pipe(ofType(onCreateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe((action) => {
                this.showSpinner = true;
                const newAnnualReportEncryptedId = action.payload.annualReportEncryptedId;
                this.annualReportProvider.setActiveAnnualReportEncryptedId(newAnnualReportEncryptedId);
                this.annualReportProvider.dispatchActiveAnnualReport(newAnnualReportEncryptedId);

                // React to the new annual report being loaded instead of arbitrary timer
                this.annualReportProvider.getActiveAnnualReport$().pipe(
                    filter(report => report != null),
                    take(1),
                    untilDestroyed(this)
                ).subscribe(() => {
                    this.showSpinner = false;
                });
            });

        // Listen for ar Templates
        this.actionsListener$
            .pipe(
                ofType(annualReportTemplateActions.getArTemplatesByFilterSuccess),
                mergeMap((arTemplatesResponse) =>
                    this.providerOfAnnualReports
                        .getAnnualReportIsSealed$()
                        .pipe(map((isSealed) => ({ arTemplatesResponse, isSealed }))),
                ),
                untilDestroyed(this),
            )
            .subscribe(({ arTemplatesResponse, isSealed }) => {
                if (!isSealed) {
                    this.handleArTemplates(arTemplatesResponse.payload);
                }
            });

        // Shows the generic error message when the AR templates could not be loaded.
        this.actionsListener$
            .pipe(ofType(annualReportTemplateActions.getArTemplatesByFilterFailed), untilDestroyed(this))
            .subscribe(() => this.fireErrorMessage('aumico_reports_error_dialog_something_went_wrong'));

        // Listen for previous annual report options for autofill
        this.annualReportProvider
            .getPreviousAnnualReportOptions$()
            .pipe(
                distinctUntilChanged((prev, curr) => fastDeepEqual(prev, curr)),
                filter((options) => options != null),
                untilDestroyed(this),
            )
            .subscribe((options) => {
                this.previousAnnualReportOptions = options;

                // Only change mode if we're not in SAVED mode and if hasPreviousData is enabled
                if (this.mode !== GeneralInformationMode.SAVED && this.generalInformationOutput?.hasPreviousData !== false) {
                    if (options.length > 0) {
                        this.mode = GeneralInformationMode.AUTOFILL;
                    } else {
                        this.mode = GeneralInformationMode.NEW;
                    }
                }
            });

        // Listen for fetching the annual report corresponding to the saved autofill id for balance
        this.annualReportProvider
            .getAnnualReportForBalanceAutoFill$()
            .pipe(
                filter((report) => report != null),
                untilDestroyed(this),
            )
            .subscribe((autoFillAnnualReport) => {
                this.annualReportForBalanceAutoFill = cloneDeep<IAnnualReport>(autoFillAnnualReport);
                // set the saved selected autofill report for display purposes

                this.savedData = {
                    ...this.savedData,
                    autofillReportForBalance: {
                        id: autoFillAnnualReport._id,
                        name: `${this.dateLocalePipe.transform(new Date(autoFillAnnualReport.generalInformation.balanceSheetDate)).toString()} (${this.dateLocalePipe.transform(new Date(autoFillAnnualReport.generalInformation.incomeCurrentYearFromDate)).toString()} - ${this.dateLocalePipe.transform(new Date(autoFillAnnualReport.generalInformation.incomeCurrentYearToDate)).toString()})`,
                    },
                };
            });

        // Listen for fetching the annual report corresponding to the saved autofill id for income
        this.annualReportProvider
            .getAnnualReportForIncomeAutoFill$()
            .pipe(
                filter((report) => report != null),
                untilDestroyed(this),
            )
            .subscribe((autoFillAnnualReport) => {
                this.annualReportForIncomeAutoFill = cloneDeep<IAnnualReport>(autoFillAnnualReport);
                // set the saved selected autofill report for display purposes

                this.savedData = {
                    ...this.savedData,
                    autofillReportForIncome: {
                        id: autoFillAnnualReport._id,
                        name: `${this.dateLocalePipe
                            .transform(new Date(autoFillAnnualReport.generalInformation.incomeCurrentYearFromDate))
                            .toString()} - ${this.dateLocalePipe
                                .transform(new Date(autoFillAnnualReport.generalInformation.incomeCurrentYearToDate))
                                .toString()} `,
                    },
                };
            });

        // Listen for fetching selected company details
        this.companiesProvider
            .getSelectedCompanyById$()
            .pipe(
                filter((company) => company != null),
                untilDestroyed(this),
            )
            .subscribe((company) => {
                this.updatedCompany = cloneDeep<ICompanyDTO>(company);
                this.selectedCompany = cloneDeep<ICompanyDTO>(company);
                // Load company-specific data (accounting bases, AR templates, previous report options)
                // in reaction to the fetched company. We must NOT call companySelectionChange here,
                // because it re-dispatches the company fetch and this subscription listens for the
                // result of that fetch — that created an infinite loop of backend requests.
                // Guard on the company id: loadCompanySpecificData triggers CountriesService calls that
                // themselves re-dispatch the company fetch, so without this guard the same company
                // re-emitting would loop and fire the AR-template / accounting-basis requests repeatedly.
                if (this.lastLoadedCompanyId !== company._id) {
                    this.lastLoadedCompanyId = company._id;
                    this.loadCompanySpecificData(this.companyMapper.mapCompanyDtoToCompanyNameIdLegalForm(company));
                }
            });

        // Listen for accounting basis selection changes and set AR template when accounting basis is selected and AR templates are available
        this.accountingBasisSelectionChangedSubject.pipe(
            filter(() => this.accountingBasisSelected != null && this.arTemplates.length > 0),
            // Add debounce to prevent rapid-fire emissions
            debounceTime(100),
            untilDestroyed(this)
        ).subscribe(() => {
            this.setArTemplate(this.accountingBasisSelected);
        });

        // Listen for previous year toggle changes to handle data reload properly
        this.hasPreviousDataChangedSubject.pipe(
            distinctUntilChanged(),
            filter(() => this.selectedCompany != null && this.currentAnnualReport != null),
            switchMap((hasPreviousData) => {
                if (hasPreviousData) {
                    // Re-enable previous year: reload previous report options
                    return of(null).pipe(
                        tap(() => {
                            this.annualReportProvider.dispatchGetPreviousAnnualReportOptions(
                                this.selectedCompany._id,
                                this.currentAnnualReport._id,
                                this.selectedCompany.legalform._id,
                                this.accountingBasisSelected?._id,
                            );
                        })
                    );
                } else {
                    // Disable previous year: clear autofill data
                    // The mode and the report options stay untouched so the form component keeps its state
                    return of(null).pipe(
                        tap(() => {
                            this.annualReportProvider.resetBalanceAutoFillId();
                            this.annualReportProvider.resetIncomeAutoFillId();
                        })
                    );
                }
            }),
            untilDestroyed(this)
        ).subscribe();


        this.projectsStore
            .select(selectActiveProjectId)
            .pipe(untilDestroyed(this))
            .subscribe((projectId) => {
                this.projectId = projectId;

            });

    }

    private loadCompanies(): void {
        debug('loadCompanies called');
        this.companiesProvider
            .getCompaniesNameIdLegalForm$()
            .pipe(untilDestroyed(this))
            .subscribe((companies: ICompanyNameIdLegalFormDTO[]) => {
                debug('Received companies:', companies.length);
                this.companies = companies;
            });
    }

    private loadAccountingBases(): void {
        debug('loadAccountingBases called');
        // Dispatch the request to fetch accounting bases
        this.settingsProvider.dispatchGetAllAccountingBasis();

        this.settingsProvider
            .allAccountingBasis$()
            .pipe(untilDestroyed(this))
            .subscribe((accountBases: IAccountingBasisExtendedDTO[]) => {
                debug('Received accounting bases:', accountBases.length, 'isCompanySpecificDataLoading:', this.isCompanySpecificDataLoading);
                // Only set accounting bases if we're not currently loading company-specific data
                // This prevents the generic load from overwriting company-specific filtered results
                if (!this.isCompanySpecificDataLoading && this.accountingBasesFilteredSelections.length === 0) {
                    debug('Setting initial accounting bases (no company-specific filtering active)');
                    this.accountingBases = this.baseObjectsMapper.getBaseLegalFormsOrAccountBases(accountBases);
                    this.rememberCountriesOfAccountingBases(accountBases);
                    this.refreshAccountingBasesSelections();
                    debug('Mapped accounting bases:', this.accountingBasesFilteredSelections.length);
                } else {
                    debug('Skipping generic accounting bases load - company-specific loading in progress or already completed');
                }
            });
    }

    private loadCurrencies(): void {
        this.settingsProvider
            .allCurrencyItem$()
            .pipe(untilDestroyed(this))
            .subscribe((currencies: ICurrencyExtendedDTO[]) => {
                this.currencies = this.baseObjectsMapper.getBaseCurrencies(currencies);
                this.currenciesFilteredSelections = this.currencies.map((currency) => ({
                    id: currency._id,
                    name:
                        currency.text?.[this.currentLanguage] ??
                        currency.text?.[this.defaultLanguage] ??
                        currency.name,
                }));
            });

        this.orchestratorOfAnnualReports.requestCurrencyFromBackend();
    }

    private loadCountries(): void {
        const retrievedCountriesByUSerLanguage: countries.LocalizedCountryNames<{ select: 'official' }> =
            this.countriesService.getCountriesByLanguage(this.currentLanguage);
        this.countries = this.countriesMapper.mapCountries(retrievedCountriesByUSerLanguage);

        this.countriesService
            .getCountryForCompanyWithNoCountry$()
            .pipe(untilDestroyed(this))
            .subscribe((initializedCountryCode: string) => this.handleCountry(initializedCountryCode));
    }

    private handleCountry(initializedCountryCode: string): void {
        this.initializedCountryCode = initializedCountryCode;
        this.onCountrySelectionChange(this.initializedCountryCode);
    }

    onCountrySelectionChange(countryCode: string | undefined) {
        const filterOptions: IFilterOptions = {
            countryISO31661Alpha2Code: countryCode,
        };
        this.settingsProvider.dispatchGetAllLegalForm(filterOptions);
    }

    private loadLegalForms(): void {
        // Dispatch the request to fetch legal forms
        this.settingsProvider.dispatchGetAllLegalForm();

        this.settingsProvider
            .allLegalForm$()
            .pipe(untilDestroyed(this))
            .subscribe((legalForms: ILegalFormExtendedDTO[]) => {
                this.shortLegalForms = this.baseObjectsMapper.getBaseLegalFormsOrAccountBases(legalForms);
            });
    }

    private loadReportLocales(): void {
        this.actionsListener$
            .pipe(
                ofType(
                    annualReportTemplateActions.getArTemplateLocalesSuccess,
                    configurationsActions.getSupportedLocalesSuccess,
                ),
            )
            .pipe(
                filter((payload) => payload.payload.length > 0),
                untilDestroyed(this),
            )
            .subscribe(({ payload }) => {
                this.locales = this.prepareReportLocales(payload);
            });
    }

    private loadReportTypes(): void {
        this.reportTypes = [
            { id: 'annual', name: this.i18next.t('aumico_create_project_dialog_select_report_type_annual') as string },
            {
                id: 'quarterly',
                name: this.i18next.t('aumico_create_project_dialog_select_report_type_quarterly') as string,
            },
            {
                id: 'monthly',
                name: this.i18next.t('aumico_create_project_dialog_select_report_type_monthly') as string,
            },
            { id: 'custom', name: this.i18next.t('aumico_create_project_dialog_select_report_type_custom') as string },
        ];
    }

    private handleActiveAnnualReport(annualReport: IAnnualReport): void {
        debug('handleActiveAnnualReport called', annualReport);
        this.currentAnnualReport = annualReport;
        this.originalGeneralInformation = annualReport.generalInformation;
        this.setupData = annualReport.setupData;

        if (
            annualReport.generalInformation == null &&
            annualReport.setupData != null &&
            this.previousAnnualReportOptions.length === EMPTY_ARRAY_LENGTH
        ) {
            // reset saved data incase redo button was clicked
            this.savedData = undefined;

            // NEW REPORT
            debug('Setting mode to NEW, setupData:', this.setupData);
            this.mode = GeneralInformationMode.NEW;
            this.selectedCompany = this.setupData.company;
            debug('Selected company from setupData:', this.selectedCompany);

            // Ensure the company selection triggers the proper flow for loading accounting bases and AR templates
            if (this.setupData.company) {
                debug('Dispatching company selection for new report');
                this.companiesProvider.dispatchCompanyById(this.setupData.company._id);
            }
        } else if (annualReport.generalInformation != null) {
            // SAVED REPORT IN EDIT MODE
            debug('Setting mode to SAVED');
            this.mode = GeneralInformationMode.SAVED;
            this.setSavedGeneralInformation(this.currentAnnualReport);
        } else if (annualReport.generalInformation == null && this.mode === GeneralInformationMode.SAVED && this.previousAnnualReportOptions.length !== EMPTY_ARRAY_LENGTH) {
            // REDO Button has been clicked after report was saved
            this.mode = GeneralInformationMode.AUTOFILL;
        }

        // Rebuilds the accounting-basis list, e.g. after REDO turned the saved report into a new one.
        this.refreshAccountingBasesSelections();

        // Set AR TEMPLATE navigation Steps and locale

        if (
            annualReport.annualReportTemplate != null &&
            annualReport.annualReportTemplate.annualReportSteps != null &&
            annualReport.annualReportTemplate.annualReportSteps.length !== EMPTY_ARRAY_LENGTH
        ) {
            this.annualReportNavigationProvider.setActiveArNavigation(
                annualReport.annualReportTemplate.annualReportSteps,
                annualReport.isSealed
            );
            this.annualReportTemplatesProvider.dispatchGetArTemplateLocale(annualReport.annualReportTemplate._id);
        } else if (annualReport.generalInformation != null) {
            this.annualReportNavigationProvider.setActiveArNavigation(
                DEFAULT_AR_NAVIGATION,
                annualReport.isSealed
            );
        }

        if (annualReport.annualReportTemplate == null) {
            this.configurationsProvider.dispatchSupportedLocalesList();
        }

        // Set report locale

        if (annualReport.locale != null) {
            this.reportLocale = this.prepareReportLocales(annualReport.locale);
        }

        // for edit mode, we set the autofill id with what was saved before
        if (annualReport.autoFillAnnualReportId != null) {
            this.annualReportProvider.setBalanceAutoFillIdWithChosenAnnualReportId(annualReport.autoFillAnnualReportId);

            this.annualReportProvider.dispatchBalanceAutoFillFromChosenOrSavedId(annualReport.autoFillAnnualReportId);
        }

        if (annualReport.autoFillAnnualReportIdForIncomeStatement != null) {
            this.annualReportProvider.setIncomeAutoFillIdWithChosenAnnualReportId(
                annualReport.autoFillAnnualReportIdForIncomeStatement,
            );

            this.annualReportProvider.dispatchIncomeAutoFillFromChosenOrSavedId(
                annualReport.autoFillAnnualReportIdForIncomeStatement,
            );
        } else if (annualReport.autoFillAnnualReportId != null) {
            // For old annual reports that do not have a separate id for income statement autofill,
            this.annualReportProvider.setIncomeAutoFillIdWithChosenAnnualReportId(annualReport.autoFillAnnualReportId);
            this.annualReportProvider.dispatchIncomeAutoFillFromChosenOrSavedId(annualReport.autoFillAnnualReportId);
        }
    }

    /**
     * REV-21315: builds a region-normalized locale (e.g. "de-CH") from a language tag.
     * getCurrentLanguageWithFallBack()/environment.defaultLanguage may return a region-less
     * tag (e.g. "en" from an /en/ URL); the previous code did `split('-')[1].toUpperCase()`
     * which threw "Cannot read properties of undefined (reading 'toUpperCase')" for those.
     * A region-less tag is mapped to the app's default full locale for that language (mirrors the
     * LANGUAGES enum: de-ch, en-us, fr-ch, it-ch), so e.g. "en" resolves to "en-US".
     */
    private toRegionLocale(language: string): string {
        const [languagePart, regionPart] = (language ?? '').split('-');
        if (regionPart) {
            return `${languagePart}-${regionPart.toUpperCase()}`;
        }
        const defaultRegionByLanguage: Record<string, string> = {
            de: 'de-CH',
            en: 'en-US',
            fr: 'fr-CH',
            it: 'it-CH',
        };
        return defaultRegionByLanguage[languagePart] ?? languagePart;
    }

    private setSavedGeneralInformation(annualReport: IAnnualReport): void {
        this.companiesProvider.dispatchCompanyById(annualReport.generalInformation.company._id);

        const companySelection: ICompanyNameIdLegalFormDTO = {
            _id: annualReport.generalInformation.company._id,
            name: annualReport.setupData?.company?.label ? annualReport.generalInformation.company.name + ' (' + annualReport.setupData?.company?.label + ')' : annualReport.generalInformation.company.name,
            legalform: annualReport.generalInformation.company.legalform,
        };

        this.selectedCompany = cloneDeep<ICompanyDTO>(annualReport.generalInformation.company);
        // else we pass the already saved company as the only option
        this.companies = cloneDeep<ICompanyNameIdLegalFormDTO[]>([companySelection]);

        const currentLocale = this.toRegionLocale(this.currentLanguage);
        const defaultLocale = this.toRegionLocale(this.defaultLanguage);

        // passthe saved accounting basis as the only option
        this.accountingBasesFilteredSelections = [
            {
                id: annualReport.generalInformation.accountingBasis._id,
                name: annualReport.generalInformation.accountingBasis.text[currentLocale.toLowerCase()]
                    ? annualReport.generalInformation.accountingBasis.text[currentLocale.toLowerCase()]
                    : annualReport.generalInformation.accountingBasis.text[defaultLocale.toLowerCase()],
            },
        ];
        // pass the saved ar template as the only option. It is mapped to the DTO shape because the selection
        // and the save path read `id`; the stored copy only has `_id`, and saving it unmapped drops the template id.
        this.arTemplates = cloneDeep<IAnnualReportTemplateDTO[]>(
            annualReport.annualReportTemplate
                ? [
                      this.annualReportTemplateMapper.mapAnnualReportTemplateToIAnnualReportTemplateDTO(
                          annualReport.annualReportTemplate,
                      ),
                  ]
                : [],
        );
        if (annualReport.annualReportTemplate) {
            this.arTemplatesOptions = [
                {
                    id: annualReport.annualReportTemplate._id,
                    name: annualReport.annualReportTemplate.caption[currentLocale]
                        ? annualReport.annualReportTemplate.caption[currentLocale]
                        : annualReport.annualReportTemplate.caption[defaultLocale],
                },
            ];
        } else {
            this.arTemplatesOptions = [];
        }

        // pass the saved locale as the only option
        this.locales = annualReport.locale
            ? [
                {
                    id: annualReport.locale,
                    name: this.i18n.t(
                        LOCALE_LOCALIZATION_KEYS_MAP.get(annualReport.locale.toLocaleLowerCase()),
                    ) as string,
                },
            ]
            : [];

        // map saved data to form data
        const getReportDisplayName = (report: IAnnualReportDto | undefined): string => {

            if (report?.generalInformation?.balanceSheetDate) {
                return `${this.dateLocalePipe.transform(new Date(report.generalInformation.balanceSheetDate)).toString()} (${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearFromDate)).toString()} - ${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearToDate)).toString()})`;
            }
            return 'Loading...'; // Fallback while data is being fetched
        };

        const getIncomeReportDisplayName = (report: IAnnualReportDto | undefined): string => {
            if (report?.generalInformation?.incomeCurrentYearFromDate) {
                return `${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearFromDate)).toString()} - ${this.dateLocalePipe.transform(new Date(report.generalInformation.incomeCurrentYearToDate)).toString()}`;
            }
            return 'Loading...'; // Fallback while data is being fetched
        };


        this.savedData = {
            company: companySelection,
            accountingBasis: {
                id: annualReport.generalInformation.accountingBasis._id,
                name: annualReport.generalInformation.accountingBasis.text[currentLocale.toLowerCase()]
                    ? annualReport.generalInformation.accountingBasis.text[currentLocale.toLowerCase()]
                    : annualReport.generalInformation.accountingBasis.text[defaultLocale.toLowerCase()],
            },
            arTemplate: annualReport.annualReportTemplate != null
                ? this.arTemplatesOptions.find((arTemp) => arTemp.id === annualReport.annualReportTemplate._id)
                : null,
            locale: this.locales?.find((loc) => loc.id === annualReport.locale),
            reportType:
                this.reportTypes.find((rt) => rt.id === annualReport.setupData?.reportType) ||
                this.reportTypes[FIRST_ELEMENT_INDEX],
            currency: {
                id: annualReport.generalInformation.currency._id,
                name: annualReport.generalInformation.currency.text[currentLocale.toLowerCase()]
                    ? annualReport.generalInformation.currency.text[currentLocale.toLowerCase()]
                    : annualReport.generalInformation.currency.text[defaultLocale.toLowerCase()],
            },
            displayCurrency: annualReport.generalInformation.displayCurrency
                ? {
                    id: annualReport.generalInformation.displayCurrency._id,
                    name: annualReport.generalInformation.displayCurrency.text[currentLocale.toLowerCase()]
                        ? annualReport.generalInformation.displayCurrency.text[currentLocale.toLowerCase()]
                        : annualReport.generalInformation.displayCurrency.text[defaultLocale.toLowerCase()],
                }
                : null,
            balanceSheetDate: new Date(annualReport.generalInformation.balanceSheetDate),
            balancePreviousYearDate: new Date(annualReport.generalInformation.balancePreviousYearDate),
            incomeCurrentYearFromDate: new Date(annualReport.generalInformation.incomeCurrentYearFromDate),
            incomeCurrentYearToDate: new Date(annualReport.generalInformation.incomeCurrentYearToDate),
            incomePreviousYearFromDate: new Date(annualReport.generalInformation.incomePreviousYearFromDate),
            incomePreviousYearToDate: new Date(annualReport.generalInformation.incomePreviousYearToDate),
            hasPreviousData: !annualReport.generalInformation.noPreviousData,
            autofillFromPreviousForBalance: annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReport,
            // for older reports that do not have a separate field for income statement, we use the same value as balance sheet
            autofillFromPreviousForIncome:
                annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement != null
                    ? annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement
                    : annualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReport,
            autofillReportForBalance: this.annualReportForBalanceAutoFill
                ? {
                    id: annualReport.autoFillAnnualReportId,
                    name: getReportDisplayName(this.annualReportForBalanceAutoFill)
                } as any
                : null,
            // for older reports that do not have a separate field for income statement, we use the same value as balance sheet
            autofillReportForIncome:
                annualReport.autoFillAnnualReportIdForIncomeStatement != null
                    ? (this.annualReportForIncomeAutoFill
                        ? { id: annualReport.autoFillAnnualReportIdForIncomeStatement, name: getIncomeReportDisplayName(this.annualReportForIncomeAutoFill) } as any
                        : null)
                    : (this.annualReportForBalanceAutoFill
                        ? { id: annualReport.autoFillAnnualReportId, name: getReportDisplayName(this.annualReportForBalanceAutoFill) }
                        : null),
        };
    }

    private handleArTemplates(arTemplates: IAnnualReportTemplateDTO[]): void {
        debug('handleArTemplates called with templates:', arTemplates.length);

        if (arTemplates.length === EMPTY_ARRAY_LENGTH) {
            debug('No AR templates available - clearing selections');
            this.arTemplateToBeSaved = null;
            this.arTemplatesOfCompany = [];
            this.accountingBasesFilteredSelections = [];

            if (!this.noArTemplateWarningDialogOpen) {
                this.openNoArTemplateWarningDialog();
            }
            this.locales = undefined;
            this.annualReportNavigationProvider.resetActiveArNavigation();
            return;
        }

        debug('Setting AR templates and filtering accounting bases');
        this.arTemplates = cloneDeep<IAnnualReportTemplateDTO[]>(arTemplates);
        this.arTemplatesOfCompany = cloneDeep<IAnnualReportTemplateDTO[]>(arTemplates);
        this.refreshAccountingBasesSelections();

        // Set AR template if accounting basis is already selected (regardless of mode)
        if (this.accountingBasisSelected != null) {
            debug('Accounting basis already selected, setting AR template');
            this.setArTemplate(this.accountingBasisSelected);
        } else {
            debug('No accounting basis selected yet');
        }
    }

    // Offers only the accounting bases that an AR template of the company uses; empty until the templates arrived.
    private refreshAccountingBasesSelections(): void {
        // A saved report keeps showing only its saved accounting basis.
        if (this.mode === GeneralInformationMode.SAVED) {
            return;
        }

        if (this.arTemplatesOfCompany == null) {
            this.accountingBasesFilteredSelections = [];
            return;
        }

        const accountingBasisIdsOfArTemplates = this.arTemplatesOfCompany
            .map((arTemplate) => arTemplate.accountingBasesIds)
            .flat();

        this.accountingBasesFilteredSelections = this.accountingBases
            .filter((accountingBasis) => accountingBasisIdsOfArTemplates.includes(accountingBasis._id))
            .filter((accountingBasis) => this.appliesInCountryOfCompany(accountingBasis._id))
            .map((accountingBasis) => ({
                id: accountingBasis._id,
                name: accountingBasis.text[this.currentLanguage],
            }));

        debug('Filtered accountingBasesFilteredSelections:', this.accountingBasesFilteredSelections.length);
    }

    // Stores the countries each received accounting basis applies in.
    private rememberCountriesOfAccountingBases(accountingBases: Array<IAccountingBasis | IAccountingBasisDTO>): void {
        this.countriesOfAccountingBases = new Map(
            accountingBases.map((accountingBasis) => [
                String(accountingBasis._id),
                accountingBasis.countryISO31661Alpha2Codes ?? [],
            ]),
        );
    }

    // True when the accounting basis applies in the country of the selected company, or that country is not known yet.
    private appliesInCountryOfCompany(accountingBasisId: string): boolean {
        const countries = this.countriesOfAccountingBases.get(accountingBasisId) ?? [];
        return this.countryOfCompany == null || countries.length === 0 || countries.includes(this.countryOfCompany);
    }

    companySelectionChange(company: ICompanyNameIdLegalFormDTO): void {
        debug('companySelectionChange called', company);
        // Only fetch the full company details here. The getSelectedCompanyById$ subscription
        // reacts to the result and triggers loadCompanySpecificData(). Loading the dependent
        // data here as well would re-dispatch this fetch from within its own result listener,
        // causing an infinite loop of backend requests.
        this.companiesProvider.dispatchCompanyById(company._id);
    }

    private loadCompanySpecificData(company: ICompanyNameIdLegalFormDTO): void {
        debug('loadCompanySpecificData called', company);

        // React to the active annual report being available before dispatching previous reports
        if (this.currentAnnualReport != null && this.currentAnnualReport.generalInformation == null) {
            debug('Waiting for annual report to be available before dispatching previous reports');
            this.annualReportProvider.getActiveAnnualReport$().pipe(
                filter(report => report != null && report._id === this.currentAnnualReport._id),
                take(1),
                untilDestroyed(this)
            ).subscribe(() => {
                debug('Annual report available, dispatching previous report options');
                this.annualReportProvider.dispatchGetPreviousAnnualReportOptions(
                    company._id,
                    this.currentAnnualReport._id,
                    company.legalform._id,
                );
            });
        }

        // Enhanced company selection handling with proper accounting basis loading
        debug('Starting accounting basis and AR template loading for company');
        this.isCompanySpecificDataLoading = true;
        this.arTemplatesOfCompany = null;
        this.countryOfCompany = null;

        // Load the company-specific accounting bases for the dropdown. take(1) on the country stream
        // resolves the country once (the underlying CountriesService stream is hot and never completes);
        // allAccountingBasis$ then keeps the dropdown in sync as the fetched bases arrive.
        this.countriesService
            .getCountryForAccountingBasis$(company._id)
            .pipe(
                take(1),
                tap((countryCode) => {
                    debug('Got country code for accounting basis:', countryCode);
                    this.countryOfCompany = countryCode;
                    const filterOptions: IFilterOptions = {
                        countryISO31661Alpha2Code: countryCode,
                    };
                    this.settingsProvider.dispatchGetAllAccountingBasis(filterOptions);
                }),
                switchMap(() => this.settingsProvider.allAccountingBasis$()),
                untilDestroyed(this),
            )
            .subscribe({
                next: (accountingBasis: IAccountingBasis[]) => {
                    debug('Received company-specific accounting basis from settings provider:', accountingBasis.length);
                    this.accountingBases = this.baseObjectsMapper.getBaseLegalFormsOrAccountBases(accountingBasis);
                    this.rememberCountriesOfAccountingBases(accountingBasis);
                    this.refreshAccountingBasesSelections();
                    this.isCompanySpecificDataLoading = false;
                },
                error: (error) => {
                    debug('Error loading accounting bases:', error);
                    this.isCompanySpecificDataLoading = false;
                },
            });

        // Dispatch the AR templates for this company exactly once. Previously this was chained after
        // allAccountingBasis$ (a hot selector) via mergeMap, so it re-fired the AR-templates request
        // every time the accounting-basis state or selected company re-emitted. take(1) on the country
        // stream guarantees a single dispatch per company load.
        this.countriesService
            .getCountryToExistingCompany$(company._id)
            .pipe(
                take(1),
                untilDestroyed(this),
            )
            .subscribe((country: string) => {
                debug('Got country for existing company, dispatching AR templates:', country);
                this.annualReportTemplatesProvider.dispatchGetArTemplatesByFilter({
                    legalFormId: company.legalform._id,
                    countryISO31661Alpha2Code: country,
                });
            });
    }

    accountingBasisSelectionChange(accountingBasis: INameId | undefined): void {
        this.accountingBasisSelected = this.accountingBases.find((ab) => ab._id === accountingBasis?.id);
        this.accountingBasisSelectionChangedSubject.next();
        this.annualReportProvider.dispatchGetPreviousAnnualReportOptions(
            this.selectedCompany._id,
            this.currentAnnualReport?._id,
            this.selectedCompany.legalform._id,
            this.accountingBasisSelected?._id,
        );

        if (accountingBasis == null) {
            this.arTemplates = [];
            this.arTemplatesOptions = [];
            this.arTemplateToBeSaved = null;
            this.annualReportNavigationProvider.resetActiveArNavigation();
            return;
        }
    }

    arTemplateSelectionChange(arTemplateId: string | undefined): void {
        this.arTemplateToBeSaved = this.arTemplates.find((template) => template.id === arTemplateId);
        if (this.arTemplateToBeSaved != null && this.arTemplateToBeSaved.annualReportSteps != null) {
            this.annualReportNavigationProvider.setActiveArNavigation(
                this.arTemplateToBeSaved.annualReportSteps,
                false
            );
            this.locales = this.prepareReportLocales(this.arTemplateToBeSaved.locales);
        } else {
            this.annualReportNavigationProvider.resetActiveArNavigation();
            this.configurationsProvider.dispatchSupportedLocalesList();

            this.configurationsProvider
                .getSupportedLocales$()
                .pipe(take(1))
                .subscribe((supportedLocales) => {
                    this.locales = this.prepareReportLocales(supportedLocales);
                });
        }
    }

    autofillReportForBalanceSelectionChange(autofillReportId: string): void {
        this.annualReportProvider.setBalanceAutoFillIdWithChosenAnnualReportId(autofillReportId);
        this.annualReportProvider.dispatchBalanceAutoFillFromChosenOrSavedId(autofillReportId);
    }

    autofillReportForIncomeSelectionChange(autofillReportId: string): void {
        this.annualReportProvider.setIncomeAutoFillIdWithChosenAnnualReportId(autofillReportId);
        this.annualReportProvider.dispatchIncomeAutoFillFromChosenOrSavedId(autofillReportId);
    }



    private setArTemplate(accountingBasis: IAccountingBasisDTO | undefined): void {
        if (this.arTemplates.length === EMPTY_ARRAY_LENGTH || accountingBasis == null) {
            return;
        }

        const currentLocale = this.toRegionLocale(this.currentLanguage);
        const defaultLocale = this.toRegionLocale(this.defaultLanguage);

        this.arTemplatesOptions = this.arTemplates
            .filter((arTemplate) => arTemplate.accountingBasesIds.includes(accountingBasis._id))
            .map((item) => ({
                id: item.id,
                name: item.caption[currentLocale] ? item.caption[currentLocale] : item.caption[defaultLocale],
            }));
    }

    private prepareReportLocales(locales: string): INameId;
    private prepareReportLocales(locales: string[]): INameId[];
    private prepareReportLocales(locales: string[] | string): INameId[] | INameId {
        if (Array.isArray(locales)) {
            return locales.map((locale) => ({
                id: locale,
                name: this.i18n.t(LOCALE_LOCALIZATION_KEYS_MAP.get(locale.toLocaleLowerCase())) as string,
            }));
        } else {
            const locale = locales;
            return {
                id: locale,
                name: this.i18n.t(LOCALE_LOCALIZATION_KEYS_MAP.get(locale.toLocaleLowerCase())) as string,
            };
        }
    }

    proceedButtonClicked = (): void => {
        this.mixpanelService.track(MIXPANEL_EVENTS.GENERAL_INFORMATION_TIME_SPENT_TILL_COMPLETION);
        this.save();
    };

    override save(): Observable<void> {
        this.isSaveRequestInProgress = true;
        if (this.originalGeneralInformation == null) {
            this.saveAnnualReport(this.selectedCompany);

            return of(void 0);
        }

        if (this.originalGeneralInformation.company == null) {
            throw new Error('company is saved with null in general information');
        }

        this.saveAnnualReport(this.originalGeneralInformation.company);

        return of(void 0);
    }

    protected override applyCompanyUpdate(company: ICompanyDTO): void {
        this.saveAnnualReport(company);
    }

    private saveAnnualReport(companyToSave: ICompanyDTO): void {
        const { locale } = this.generalInformationOutput;
        // Guard: use the form's locale if present; fall back to the stored reportLocale; finally keep the existing report locale.
        this.currentAnnualReport.locale = (locale ?? this.reportLocale)?.id ?? this.currentAnnualReport.locale;
        this.currentAnnualReport.generalInformation = this.prepareDataForSaving(companyToSave);

        if (this.currentAnnualReport.setupData != null) {
            // update company inside setupData
            this.currentAnnualReport.setupData.company = companyToSave;
            //update report type inside setupData incase user changed it
            this.currentAnnualReport.setupData.reportType = this.generalInformationOutput.reportType.id as ReportType;
            // update balance sheet date inside setupData incase user changed it
            this.currentAnnualReport.setupData.balanceSheetDate =
                this.generalInformationOutput.balanceSheetDate.toISOString();
        }

        // Without a previous period the report has no autofill source at all, so a link saved earlier
        // is dropped here as well — the assignments below only ever set an id, never clear one.
        if (!this.generalInformationOutput.hasPreviousData) {
            this.currentAnnualReport.autoFillAnnualReportId = null;
            this.currentAnnualReport.autoFillAnnualReportIdForIncomeStatement = null;
        }

        if (this.generalInformationOutput.autofillReportForBalance != null) {
            this.currentAnnualReport.autoFillAnnualReportId = this.generalInformationOutput.autofillReportForBalance.id;
        }

        if (this.generalInformationOutput.autofillReportForIncome != null) {
            this.currentAnnualReport.autoFillAnnualReportIdForIncomeStatement =
                this.generalInformationOutput.autofillReportForIncome.id;
        }

        if (this.arTemplateToBeSaved != null) {
            this.currentAnnualReport.annualReportTemplate =
                this.annualReportTemplateMapper.mapIAnnualReportTemplateDTOToAnnualReportTemplate(
                    this.arTemplateToBeSaved,
                );
        }

        if (this.mode === GeneralInformationMode.AUTOFILL && this.generalInformationOutput.reportType.id === ReportType.ANNUAL) {

            // If this is the first annual report and it autofills from a quarterly or monthly report, we need to copy the upload and mapping
            const balanceAutoFillReportType = this.annualReportForBalanceAutoFill?.setupData?.reportType;
            const balanceAutoFillIsSubAnnual =
                balanceAutoFillReportType === ReportType.QUARTERLY ||
                balanceAutoFillReportType === ReportType.MONTHLY ||
                balanceAutoFillReportType === ReportType.CUSTOM;
            if (
                this.annualReportForBalanceAutoFill?.finStatements != null &&
                this.annualReportForBalanceAutoFill.finStatements.length >= 2 &&
                balanceAutoFillIsSubAnnual
            ) {
                this.currentAnnualReport.finStatements = cloneDeep<IFinStatements[]>(this.annualReportForBalanceAutoFill.finStatements);
            }

            const incomeAutoFillReportType = this.annualReportForIncomeAutoFill?.setupData?.reportType;
            const incomeAutoFillIsSubAnnual =
                incomeAutoFillReportType === ReportType.QUARTERLY ||
                incomeAutoFillReportType === ReportType.MONTHLY ||
                incomeAutoFillReportType === ReportType.CUSTOM;
            if (
                this.annualReportForIncomeAutoFill?.finStatements != null &&
                this.annualReportForIncomeAutoFill.finStatements.length >= 2 &&
                incomeAutoFillIsSubAnnual &&
                this.currentAnnualReport.finStatements != null
            ) {
                this.currentAnnualReport.finStatements[templateConstants.incomeTemplate] = cloneDeep<IFinStatements>(this.annualReportForIncomeAutoFill.finStatements[templateConstants.incomeTemplate]);
            }
        }


        this.mixpanelService.track(MIXPANEL_EVENTS.ACCOUNTING_BASIS_SELECTION, {
            accountingBasis: this.currentAnnualReport.generalInformation.accountingBasis?.name,
        });

        this.mixpanelService.track(MIXPANEL_EVENTS.Annual_REPORT_TEMPLATE_SELECTION, {
            accountingBasis:
                this.currentAnnualReport.annualReportTemplate != null
                    ? this.currentAnnualReport.annualReportTemplate.name
                    : null,
        });

        this.dispatchSaveAnnualReport(this.currentAnnualReport);
    }

    private prepareDataForSaving(companyToSave: ICompanyDTO): IGeneralInformation {
        const generalInformationToBeSaved: IGeneralInformation = {
            company: companyToSave,
            accountingBasis: this.accountingBases.find(
                (ab) => ab._id === this.generalInformationOutput.accountingBasis.id,
            ),
            balanceSheetDate: this.generalInformationOutput.balanceSheetDate.toISOString(),
            balancePreviousYearDate: this.generalInformationOutput.hasPreviousData && this.generalInformationOutput.balancePreviousYearDate
                ? this.generalInformationOutput.balancePreviousYearDate.toISOString()
                : null,
            incomeCurrentYearFromDate: this.generalInformationOutput.incomeCurrentYearFromDate.toISOString(),
            incomeCurrentYearToDate: this.generalInformationOutput.incomeCurrentYearToDate.toISOString(),
            incomePreviousYearFromDate: this.generalInformationOutput.hasPreviousData && this.generalInformationOutput.incomePreviousYearFromDate
                ? this.generalInformationOutput.incomePreviousYearFromDate.toISOString()
                : null,
            incomePreviousYearToDate: this.generalInformationOutput.hasPreviousData && this.generalInformationOutput.incomePreviousYearToDate
                ? this.generalInformationOutput.incomePreviousYearToDate.toISOString()
                : null,
            currency: this.currencies.find((cur) => cur._id === this.generalInformationOutput.currency.id),
            displayCurrency: this.generalInformationOutput.displayCurrency
                ? this.currencies.find((cur) => cur._id === this.generalInformationOutput.displayCurrency.id)
                : undefined,
            noPreviousData: !this.generalInformationOutput.hasPreviousData,
            isAutoFillFromPreviousAnnualReportEnabled:
                this.generalInformationOutput.autofillFromPreviousForBalance ||
                this.generalInformationOutput.autofillFromPreviousForIncome,
            isPreviousDataTakenFromPreviousAnnualReport: this.generalInformationOutput.autofillFromPreviousForBalance,
            isPreviousDataTakenFromPreviousAnnualReportForIncomeStatement:
                this.generalInformationOutput.autofillFromPreviousForIncome,
        };

        return cloneDeep<IGeneralInformation>(generalInformationToBeSaved);
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (this.originalGeneralInformation == null && this.disableProceedButton === false) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else {
            return of(!this.isGeneralInformationObjectsEqual());
        }
    }

    private isGeneralInformationObjectsEqual(): boolean {
        if (this.generalInformationOutput == null) {
            return true;
        }

        if (this.originalGeneralInformation == null && this.generalInformationOutput == null) {
            return true;
        }
        if (this.reportLocale != null && this.generalInformationOutput.locale?.id !== this.reportLocale.id) {
            return false;
        }

        if (this.originalGeneralInformation == null) {
            return false;
        }

        // check only on editable properties
        const isGeneralInfoCompanySame =
            this.originalGeneralInformation.company._id === this.generalInformationOutput.company._id;

        const isReportTypeSame =
            this.setupData == null ||
            this.setupData.reportType == null ||
            this.setupData.reportType === this.generalInformationOutput.reportType.id;
        const isCurrencySame =
            this.originalGeneralInformation.currency._id === this.generalInformationOutput.currency.id;
        const isDisplayCurrencySame =
            (this.originalGeneralInformation.displayCurrency?._id ?? null) ===
            (this.generalInformationOutput.displayCurrency?.id ?? null);
        const isBalanceSheetDateSame =
            new Date(this.originalGeneralInformation.balanceSheetDate).getTime() ===
            new Date(this.generalInformationOutput.balanceSheetDate).getTime();
        const isIncomeCurrentYearFromDateSame =
            new Date(this.originalGeneralInformation.incomeCurrentYearFromDate).getTime() ===
            new Date(this.generalInformationOutput.incomeCurrentYearFromDate).getTime();
        const isIncomeCurrentYearToDateSame =
            new Date(this.originalGeneralInformation.incomeCurrentYearToDate).getTime() ===
            new Date(this.generalInformationOutput.incomeCurrentYearToDate).getTime();
        const isBalancePreviousYearDateSame =
            new Date(this.originalGeneralInformation.balancePreviousYearDate).getTime() ===
            new Date(this.generalInformationOutput.balancePreviousYearDate).getTime();
        const isIncomePreviousYearFromDateSame =
            new Date(this.originalGeneralInformation.incomePreviousYearFromDate).getTime() ===
            new Date(this.generalInformationOutput.incomePreviousYearFromDate).getTime();
        const isIncomePreviousYearToDateSame =
            new Date(this.originalGeneralInformation.incomePreviousYearToDate).getTime() ===
            new Date(this.generalInformationOutput.incomePreviousYearToDate).getTime();
        return (
            isGeneralInfoCompanySame &&
            isReportTypeSame &&
            isCurrencySame &&
            isDisplayCurrencySame &&
            isBalanceSheetDateSame &&
            isIncomeCurrentYearFromDateSame &&
            isIncomeCurrentYearToDateSame &&
            isBalancePreviousYearDateSame &&
            isIncomePreviousYearFromDateSame &&
            isIncomePreviousYearToDateSame
        );
    }

    protected onSaveSuccessfully(): void {
        this.mode = GeneralInformationMode.SAVED;
        this.originalGeneralInformation = cloneDeep<IGeneralInformation>(this.currentAnnualReport.generalInformation);
        this.isSaveRequestInProgress = false;

        this.setSavedGeneralInformation(this.currentAnnualReport);

        this.savedSuccessfully.next(true);

        this.fireSuccessNotification();
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    protected handleErrors(error: HttpErrorResponse): void {
        if (error == null) {
            this.isBackendReturnError = false;
        }
        debug(error);
        if (error != null && error.status !== 404) {
            this.isBackendReturnError = true;
            this.backendErrors = Errors.ServerError;
        }
        if (this.locales == null && error?.status === 404) {
            this.configurationsProvider.dispatchSupportedLocalesList();
        }
    }

    private openNoArTemplateWarningDialog(): void {
        this.noArTemplateWarningDialogOpen = true;

        const dialogRef = this.dialog.open<ModalComponent, ModalData>(ModalComponent, {
            disableClose: false,
            panelClass: 'custom-modalbox',
            data: {
                showCloseButton: true,
                titleIcon: ModalTitleIcons.DEFAULT,
                titleLocalizationKey: 'aumico_general_information_no_template_for_your_combination',
                width: '850px',
                content: {
                    text: [
                        'aumico_general_information_no_template_for_your_combination_content_1',
                        'aumico_general_information_no_template_for_your_combination_content_2',
                    ],
                },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe(() => (this.noArTemplateWarningDialogOpen = false));
    }

    onFormDataChanged(generalInformationData: IGeneralInformationV2Output): void {
        debug('Form data changed:', generalInformationData);

        // Check if hasPreviousData changed
        const previousHasPreviousData = this.generalInformationOutput?.hasPreviousData;
        const currentHasPreviousData = generalInformationData.hasPreviousData;

        this.generalInformationOutput = cloneDeep<IGeneralInformationV2Output>(generalInformationData);

        // Emit previous year change if it actually changed
        if (previousHasPreviousData !== currentHasPreviousData) {
            this.hasPreviousDataChangedSubject.next(currentHasPreviousData);
        }
    }

    onFormValidChanged(isValid: boolean): void {
        debug('Form validity changed:', isValid);
        this.disableProceedButton = !isValid;
        // Update any footer buttons or navigation states
        if (this.footerExtraButtonActions && this.footerExtraButtonActions.length > 0) {
            this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = !isValid;
        }
    }

    onArchiveAndGenerateNewReport() {
        // getting the project ID from the URL -> So we do not need to fetch it from the backend
        let projectId: string;
        const match = window.location.pathname.match(/project\/([a-f0-9-]+)/i);
        if (match && match[1]) {
            projectId = match[1];
        } else {
            return;
        }

        // Select the encrypted ID from the store
        this.annualReportProvider
            .getActiveAnnualReportEncryptedId$()
            .pipe(take(1))
            .subscribe((encryptedId) => {
                if (encryptedId) {
                    this.annualReportProvider.dispatchArchiveAnnualReports([encryptedId]);
                    this.annualReportProvider.createAnnualReport(this.setupData, encryptedId, projectId);
                }
            });
    }


    protected override shouldShowDeleteProjectOption(): boolean {
        // Delete incomplete interim reports only (no general information saved yet).
        // REV-21425: reportType can still be null while the form output exists (report type not yet
        // selected), so guard it with optional chaining before reading `.id` — otherwise opening the
        // unsaved-changes dialog throws "Cannot read properties of null (reading 'id')".
        const reportTypeId = this.generalInformationOutput?.reportType?.id;
        return this.originalGeneralInformation == null &&
            this.generalInformationOutput != null &&
            (reportTypeId === ReportType.QUARTERLY || reportTypeId === ReportType.MONTHLY || reportTypeId === ReportType.CUSTOM);
    }


    private handleDeleteProject(): void {

        if (this.projectId) {
            this.projectsStore.dispatch(ProjectsOverviewActions.deleteProject({ projectId: this.projectId }));
        } else {
            this.projectsStore.dispatch(ProjectsOverviewActions.projectDeleteFailure({
                error: new HttpErrorResponse({ error: 'Project ID is missing', status: 400, statusText: 'Bad Request' })
            }));
        }
    }

    /**
     * Override from BasePageComponent to wait for delete completion before navigation
     */
    protected override waitForDeleteProjectCompletion(): Observable<boolean> {
        // Trigger the delete
        this.handleDeleteProject();

        // Wait for either success or failure
        return this.actionsListener$.pipe(
            ofType(
                ProjectsOverviewActions.projectDeleted,
                ProjectsOverviewActions.projectDeleteFailure
            ),
            take(1), // Take only the first emission
            map((action) => {
                // Return true if deleted successfully, false if failed
                return action.type === ProjectsOverviewActions.projectDeleted.type;
            }),
            untilDestroyed(this)
        );
    }
}
